/**
 * The World's systems (types.ts), in the order their hooks run: within every hook point the World
 * calls them top to bottom. The order is part of the rules (it decides which log line comes first
 * and which state a later system sees), so it is spelled out here, never discovered.
 *
 * To add a system: write `systems/<name>.ts` (a class implementing `WorldSystem`, built from the
 * `SystemHost`) and add one line below, where its hooks should run.
 */
import { ActivitySystem } from './activity';
import { GmSystem } from './gm';
import { CodexSystem } from './codex';
import { SeamGateSystem } from './seamGate';
import { FastTravelSystem } from './fastTravel';
import { BreakthroughSystem } from './breakthrough';
import { AnimalsSystem } from './animals';
import { ArenaSystem } from './arena';
import { DuelGroundSystem } from './duelGround';
import { PvpDuelSystem } from './pvpDuel';
import { DuelMatchSystem } from './duelMatch';
import { DuelLockSystem } from './duelLock';
import { DuelLockOpenSystem } from './duelLockOpen';
import { DuelBotLockSystem } from './duelBotLock';
import { DuelRingOutSystem } from './duelRingOut';
import { DuelBotRingSystem } from './duelBotRing';
import { DuelRecoverySystem } from './duelRecovery';
import { DuelGuardDropSystem } from './duelGuardDrop';
import { LockLookSystem } from './lockLook';
import { DuelArmorSystem } from './duelArmor';
import { DuelKnockSystem } from './duelKnock';
import { DuelPushSystem } from './duelPush';
import { LongMoveSystem } from './longMove';
import { DuelBotArmorSystem } from './duelBotArmor';
import { DuelMoveSystem } from './duelMove';
import { DuelAirSystem } from './duelAir';
import { DuelSlideSystem } from './duelSlide';
import { DuelBotSlideSystem } from './duelBotSlide';
import { DummiesSystem } from './dummies';
import { HitWeightSystem } from './hitWeight';
import { MoveLocksSystem } from './moveLocks';
import { DungeonSystem } from './dungeon';
import { GuildSystem } from './guild';
import { GuildWarSystem } from './guildWar';
import { GuildBannerSystem } from './guildBanner';
import { SafeZonesSystem } from './safeZones';
import { PocketsSystem } from './pockets';
import { SmallDungeonsSystem } from './smallDungeons';
import { CultivationSystem } from './cultivation';
import { DuelWinBoostSystem } from './duelWinBoost';
import { SectDanceSystem } from './sectDance';
import { FishBiteSystem } from './fishBite';
import { ChopBeatSystem } from './chopBeat';
import { AutoGatherSystem } from './autoGather';
import { StationsSystem } from './stations';
import { DailyEventsSystem } from './dailyEvents';
import { ModesSystem } from './modes';
import { MeditationSystem } from './meditation';
import { QiFlowSystem } from './qiFlow';
import { GuardBuffSystem } from './guardBuff';
import { StatusRulesSystem } from './statusRules';
import { LateWoundSystem } from './lateWound';
import { LockOnSystem } from './lockOn';
import { NpcMemorySystem } from './npcMemory';
import { NpcTalkSystem } from './npcTalk';
import { NpcActsSystem } from './npcActs';
import { NpcLifeSystem } from './npcLife';
import { PlainsStorySystem } from './plainsStory';
import { Ch2StorySystem } from './ch2Story';
import { OriginStorySystem } from './originStory';
import { SectJoinSystem } from './sectJoin';
import { WarehouseSystem } from './warehouse';
import { DailyNpcSystem } from './dailyNpc';
import { ElitesSystem } from './elites';
import { WildMastersSystem } from './wildMasters';
import { BountiesSystem } from './bounties';
import { SectNeigongSystem } from './sectNeigong';
import { WanderersSystem } from './wanderers';
import { TournamentSystem } from './tournament';
import { NavSystem } from './nav';
import { AutoQuestSystem } from './autoQuest';
import { OfflineWorkSystem } from './offlineWork';
import { StoreSystem } from './store';
import { TradesSystem } from './trades';
import { RecruitersSystem } from './recruiters';
import { SkillBarSystem } from './skillBar';
import { TeachersSystem } from './teachers';
import { TrainingSystem } from './training';
import { WeatherSystem } from './weather';
import { SecretsSystem } from './secrets';
import { RedLanternSystem } from './redLantern';
import { HiddenMasterSystem } from './hiddenMaster';
import { WorldEventsSystem } from './worldEvents';
import { WorldBossesSystem } from './worldBosses';
import { QinggongDropsSystem } from './qinggongDrops';
import { AutoRulesSystem } from './autoRules';
import { IdleSitSystem } from './idleSit';
import { QiPoolSystem } from './qiPool';
import { DuelPaySystem } from './duelPay';
import { SectActivitiesSystem } from './sectActivities';
import { FameBoardsSystem } from './fameBoards';
import { TrialsSystem } from './trials';
import { WalkModeSystem } from './walkMode';
import { SlideSystem } from './slide';
import { WindSystem } from './wind';
import { WaterSystem } from './water';
import { ClimbSystem } from './climb';
import { ItemsSystem } from './items';
import { WardrobeSystem } from './wardrobe';
import { SocialSystem } from './social';
import { RedRoamersSystem } from './redRoamers';
import { GearCombatSystem } from './gearCombat';
import { ItemCraftSystem } from './itemCraft';
import { AimAssistSystem } from './aimAssist';
import { PhasesSystem } from './phases';
import type { SystemHost, WorldSystem } from './types';

export function createSystems(h: SystemHost) {
  return {
    // refuse (first: it sees every command the player sends, even one another system swallows), observe, commands
    // (navTo, navStop), step: the auto-navigation's walk (นำทาง)
    nav: new NavSystem(h),
    // commands (autoQuest), observe, step: the "ต่อไป" auto-quest (systems/autoQuest.ts)
    autoQuest: new AutoQuestSystem(h),
    // refuse (a locked move's slot: before the lock-on takes the command), commands (learnMove): systems/moveLocks.ts
    moveLocks: new MoveLocksSystem(h),
    // refuse (weapons, skills and jumps in deep water), playerStep (a swimmer lets go of guard and fight): systems/water.ts
    water: new WaterSystem(h),
    // refuse (weapons and skills on a cliff face): systems/climb.ts
    climb: new ClimbSystem(h),
    modes: new ModesSystem(h),
    // regenMult, commands
    meditation: new MeditationSystem(h),
    // playerStep (out-of-combat and meditation refill), observe (basic cuts and blocks give qi): systems/qiFlow.ts
    qiFlow: new QiFlowSystem(h),
    // observe (a blocked blow): a guard buff's arming becomes its effect (data/guardBuffs.ts)
    guardBuff: new GuardBuffSystem(h),
    // step (stun mirrored as มึนงง, immunity, bleed per metre), observe (heavy hit wakes, PvP diminishing returns): systems/statusRules.ts
    statusRules: new StatusRulesSystem(h),
    // observe (status), step: the delayed wound opening when its timer runs out (status `woundPct`, one-cut)
    lateWound: new LateWoundSystem(h),
    // refuse (a held `skill` asks for a lock, a tap takes the cone ahead), observe (the move starts: locked), step
    // (face, grace, drop): ท่าล็อกเป้า (systems/lockOn.ts)
    lockOn: new LockOnSystem(h),
    // playerStep: the training spot underfoot, then the 30 s payout
    cultivation: new CultivationSystem(h),
    // step, observe: E4 five phases, overcoming opens +0.2 s, generating switch, PvE x1.15 (systems/phases.ts)
    phases: new PhasesSystem(h),
    // observe: a duel win lifts training, 5 foes a real day (systems/duelWinBoost.ts)
    duelWinBoost: new DuelWinBoostSystem(h),
    // H23 sect dance: a 15-min window every 4 real hours in each sect map (systems/sectDance.ts)
    sectDance: new SectDanceSystem(h),
    // step, cast/strike: fishing bite tell + strike window (systems/fishBite.ts); chopBeat: 3-hit woodcut rhythm
    fishBite: new FishBiteSystem(h),
    chopBeat: new ChopBeatSystem(h),
    // commands, step: the profession repeat loop, ok grade x0.7, half-rate budget (systems/autoGather.ts)
    autoGather: new AutoGatherSystem(h),
    // anvil workstation test (systems/stations.ts): buy, place, real-time forge job, raid
    stations: new StationsSystem(h),
    // step: training, then professions, then the ring (the order world.ts ran them in)
    training: new TrainingSystem(h),
    trades: new TradesSystem(h),
    arena: new ArenaSystem(h),
    // setup (the ring master and the regulars), commands (duelChallenge), talk, step: open challenges in the
    // duelling ground, fought on the ring's Duel (data/duelGround.ts); the one challenged back after the bout
    duelGround: new DuelGroundSystem(h),
    // setup, commands (duelAnswer), step: a duel between two real players in a World of their own (data/pvpDuel.ts)
    pvpDuel: new PvpDuelSystem(h),
    // setup (the hub rival), refuse (claims the challenge of that rival), commands (duelMatch), step: the best-of-three bout vs a stand-in player (data/duelMatch.ts)
    duelMatch: new DuelMatchSystem(h),
    // setup (the form gate, the bout's hit rules), refuse (ดิ้นหลุด), step: lock forms only on an opened foe, routines (P3)
    duelLockOpen: new DuelLockOpenSystem(h),
    // setup, commands (lockOn), step: the persistent lock-on, Tab or a click (round 4, opt-in: data/duelMatch.ts DUEL_LOCK)
    duelLock: new DuelLockSystem(h),
    // refuse, observe, step: recovery after every move, the whiffed break, the break numbers of the bout (data/moveTiming.ts)
    duelRecovery: new DuelRecoverySystem(h),
    // refuse: letting a guard go costs a few frames of recovery in the bout (data/guardRead.ts)
    duelGuardDrop: new DuelGuardDropSystem(h),
    // setup (World.armourHook, the bout's hit rules), step: ตัวแดง / ตัวเหลือง, armour windows on moves (data/duelArmor.ts)
    duelArmor: new DuelArmorSystem(h),
    // setup (the bout's hit rules), refuse, step: กระเด็น / ล้ม / ลุก, throws, knock-downs and get-ups (data/duelKnock.ts)
    duelKnock: new DuelKnockSystem(h),
    // setup (the bout's hit rules), step: ดันถอย, push moves drive the struck one back through a guard (data/duelPush.ts)
    duelPush: new DuelPushSystem(h),
    // setup (ults scripted + armoured), refuse (held, full bar), observe (tell, opener), step: ท่าใหญ่ set pieces and the
    // long-move beat events, lock routines included (systems/longMove.ts, data/longMoves)
    longMove: new LongMoveSystem(h),
    // refuse, observe, step: the bout's steps (i-frames, direction, stop), landings, the combo escape (data/duelMatch.ts DUEL_MOVE)
    duelMove: new DuelMoveSystem(h),
    // refuse, step: the jump-in dive strike (ท่าโฉบ) after an air dash
    duelAir: new DuelAirSystem(h),
    // setup (the slide tuner, the bout's hit rules), refuse, observe, step: สไลด์ in the bout (data/slide.ts DUEL_SLIDE)
    duelSlide: new DuelSlideSystem(h),
    // step: the bout bot's slides (evade, out of a corner, gap-close with a slide strike)
    duelBotSlide: new DuelBotSlideSystem(h),
    // step: the bout bot's lock forms (punishes on an opened foe, escapes by level)
    duelBotLock: new DuelBotLockSystem(h),
    // step: out of the ring (one continuous stay out > 10 s loses; the one-bout format, data/duelMatch.ts DUEL_FORMAT)
    duelRingOut: new DuelRingOutSystem(h),
    // (no hooks: the bot's edge and heal filter, BOT_ACT_FILTERS)
    duelBotRing: new DuelBotRingSystem(h),
    // step: the bout bot and armour (no trades into it) and its get-ups (systems/duelBotArmor.ts)
    duelBotArmor: new DuelBotArmorSystem(h),
    // refuse (lockOn look), step after the bout's own facing (lock, step stops, knocks): locked with the right button held, face the camera's way
    lockLook: new LockLookSystem(h),
    // refuse: a skill's aim is pulled to the locked foe inside a cone in a bout (systems/aimAssist.ts)
    aimAssist: new AimAssistSystem(h),
    // setup, observe, step: the training yard's dummies (หุ่นไม้) that never fall (data/dummies.ts)
    dummies: new DummiesSystem(h),
    // observe (hit), step: the knock-back and stagger of a landed blow (data/hitWeight.ts)
    hitWeight: new HitWeightSystem(h),
    // observe (in and out of the cave, cast starts, scripted moves, hits, deaths), monsterDecide, talk (the helpers'
    // herald, the villagers), step: ถ้ำคลื่นคำราม's run (data/dungeon.ts)
    dungeon: new DungeonSystem(h),
    // setup, step: into or out of a safe zone (data/safeZones.ts), monsters giving up the chase there
    safeZones: new SafeZonesSystem(h),
    // setup: the 256 m plains' monster pockets (data/layouts/plains.ts POCKETS) beyond the zone's own spawns
    pockets: new PocketsSystem(h),
    smallDungeons: new SmallDungeonsSystem(h),
    // regenMult (the guild level's buff), commands: กิลด์ (systems/guild.ts)
    guild: new GuildSystem(h),
    // commands (sign-up, the clock), step: ศึกกิลด์ every day 20:00–20:30 (systems/guildWar.ts)
    guildWar: new GuildWarSystem(h),
    // commands (enemy list, raise), step (the clock, simulated hunters), pvpHostile, pkKill: ป้ายสังหาร (systems/guildBanner.ts)
    guildBanner: new GuildBannerSystem(h),
    // commands only
    store: new StoreSystem(h),
    // setup and talk: the hall of the six schools' recruiters, then the qinggong teachers
    recruiters: new RecruitersSystem(h),
    teachers: new TeachersSystem(h),
    // step (the weather turns) and its GM command
    weather: new WeatherSystem(h),
    // no hooks: the updrafts the World's glide asks about (`liftAt`)
    wind: new WindSystem(h),
    // step: the daily events by the clock (data/dailyEvents.ts), their people and acts set before npcActs picks
    dailyEvents: new DailyEventsSystem(h),
    // setup (the act people), step: every townsperson's act for the moment (data/npcActs.ts), after the
    // weather has turned and before the activity cells move on
    npcActs: new NpcActsSystem(h),
    // asleep (the World's think loop asks it), step: the active 32 m cells round the players for
    // the next step, and a region's wild grounds set down when an active cell first touches them
    activity: new ActivitySystem(h),
    // setup (the village animals, after the zone's folk), step (where fights are), thinkCritter (their brains)
    animals: new AnimalsSystem(h),
    // observe, commands: what townsfolk remember of the player (before the talk reads it)
    npcMemory: new NpcMemorySystem(h),
    // observe, folkLine, step (the act people): what townsfolk say, for the moment and the player
    npcTalk: new NpcTalkSystem(h),
    // setup (offline bodies, black-market buyers, waking after the time away), refuse, commands, talk, observe
    // (blows and carriers struck), step: the blow, the sack and the bodies' work (docs/systems/OFFLINE_WORK.md)
    offlineWork: new OfflineWorkSystem(h),
    // commands (skill, barSet, barSwap), observe: the skill bar's slots and the 3 s wait between sets
    skillBar: new SkillBarSystem(h),
    // commands (walkMode): เดินช้า / เดินเร็ว, the walking pace in a zone that walks
    walkMode: new WalkModeSystem(h),
    // refuse (a jump before the end window), commands (slide), playerStep (the skid along the ground): systems/slide.ts
    slide: new SlideSystem(h),
    // setup (the smiths), talk (the forge), refuse (forging away from a smith), commands (bag, drops, pick-up, the
    // hidden weapon), playerStep (walking over loot), step (loot fading): systems/items.ts
    items: new ItemsSystem(h),
    // commands (wardrobeWear, wardrobeShow, wardrobeDye, the saved sets): ตู้เสื้อผ้า, purely cosmetic
    wardrobe: new WardrobeSystem(h),
    // commands (trialStart, trialQuit), step: the qinggong time trials (data/trials.ts)
    trials: new TrialsSystem(h),
    // step: the vistas and chests found by standing in them (data/secrets.ts)
    secrets: new SecretsSystem(h),
    // step: the red lantern (โคมแดง): rolls, gifts and the four notes (data/redLantern.ts)
    redLantern: new RedLanternSystem(h),
    // step: the hidden master's gate (four vistas) and his descent test (data/hiddenMaster.ts)
    hiddenMaster: new HiddenMasterSystem(h),
    // step, observe: the 15-minute cycle of town events (data/worldEvents.ts)
    worldEvents: new WorldEventsSystem(h),
    // step, observe, monsterDecide: the world bosses on the 3-hour rotation (data/worldBosses.ts)
    worldBosses: new WorldBossesSystem(h),
    // refuse (training a dropped art past its self cap), observe, step: the qinggong manuals and pages (data/qinggongDrops.ts)
    qinggongDrops: new QinggongDropsSystem(h),
    // commands (sectAct, sectBind, sectRead), step, observe: sect activities and manual pages (data/sectActivities.ts)
    sectActivities: new SectActivitiesSystem(h),
    // step, observe, setNow: weekly fame boards ทำเนียบยุทธภพ (data/fameBoards.ts)
    fameBoards: new FameBoardsSystem(h),
    // commands (friends, sworn enemies), observe (a red name's killing blow), step (sworn enemies in view): systems/social.ts
    // setup-free: auto-play zone rule, daily budget drain, yield x0.6 (systems/autoRules.ts, autoBudget.ts)
    autoRules: new AutoRulesSystem(h),
    // refuse (any input stands them up), step (60 s idle in a safe zone sits to meditate): systems/idleSit.ts
    idleSit: new IdleSitSystem(h),
    // observe (kills fill the pool), rate (meditation converts it): combat qi pool (systems/qiPool.ts)
    qiPool: new QiPoolSystem(h),
    // observe: ring bouts pay cultivation into the pool (systems/duelPay.ts)
    duelPay: new DuelPaySystem(h),
    social: new SocialSystem(h),
    // step (the roll, the warning, PK on and off, leaving), observe (his death): ชื่อแดงพเนจร (systems/redRoamers.ts)
    redRoamers: new RedRoamersSystem(h),
    // step, observe (status chance and resist of gear affixes); its hit helpers are called by world.ts (systems/gearCombat.ts)
    gearCombat: new GearCombatSystem(h),
    // commands: the items v2 smith actions (ล้างค่า, ขัดเกลา, ผนึก, ฝังหยก, เจาะรู, รวมหยก, แยกชิ้น; systems/itemCraft.ts)
    itemCraft: new ItemCraftSystem(h),
    // setup (the plains' people), step (needs, acts, walking, rumours; full near the player, 1 Hz far), observe (what the
    // player did, as rumours): NPC daily life (systems/npcLife.ts, data/npcLife.ts)
    npcLife: new NpcLifeSystem(h),
    // setup (the story's people), talk (givers other than the elder), refuse (the fair's choices), observe, step: origin
    // A's first hour on the 256 m plains (systems/plainsStory.ts, data/plainsOpening.ts); idle elsewhere
    plainsStory: new PlainsStorySystem(h),
    // setup, talk, step, observe: chapter 2's starter chain on town-b (systems/ch2Story.ts, data/story/ch2.ts)
    ch2Story: new Ch2StorySystem(h),
    // setup, talk, step, observe: the horse and the bamboo log in every origin's story (systems/originStory.ts, data/story/originChains.ts)
    originStory: new OriginStorySystem(h),
    // talk, refuse: a sect's seniors offer the join in its map (systems/sectJoin.ts)
    sectJoin: new SectJoinSystem(h),
    // observe (a keeper's greeting opens the store), step (closes out of reach), commands (wh*): โรงคลัง (systems/warehouse.ts)
    warehouse: new WarehouseSystem(h),
    // setup, talk: the daily-activity keeper in every town and city (systems/dailyNpc.ts, data/dailyNpc.ts)
    dailyNpc: new DailyNpcSystem(h),
    // setup (2 % of the region's table monsters), observe (extra pay): ตัวหัวหน้า (systems/elites.ts, data/bounties.ts)
    elites: new ElitesSystem(h),
    // setup/step (about 1 per region per hour), talk (challenge), observe (mark on a win): ยอดฝีมือพเนจร (systems/wildMasters.ts)
    wildMasters: new WildMastersSystem(h),
    // observe (kills count), claim on the keeper's talk: ประกาศล่ารายวัน (systems/bounties.ts)
    bounties: new BountiesSystem(h),
    // observe (kills per family, tiers pay once), mul (damage vs a family): ทะเบียนสัตว์ร้าย (systems/codex.ts)
    codex: new CodexSystem(h),
    // H18: tier 2/3 sect neigong open in order (systems/sectNeigong.ts)
    sectNeigong: new SectNeigongSystem(h),
    // step: the plains' wanderers (simulated players): online by the hours, goals by personality, town chat, ring
    // challenges (systems/wanderers.ts, data/wanderers.ts)
    wanderers: new WanderersSystem(h),
    // setup (the herald), talk, commands (tourney), step: the weekly tournament ประลองยุทธภพ and the legendary palm's holder (systems/tournament.ts, data/tournament.ts)
    tourney: new TournamentSystem(h),
    // refuse (every command during a ride), step, setup: seam gate rides between regions (systems/seamGate.ts, data/regions.ts)
    seamGate: new SeamGateSystem(h),
    // setup (discover the zone's station), commands (fastTravel), step (cooldown): paid warps (systems/fastTravel.ts)
    fastTravel: new FastTravelSystem(h),
    // refuse (the guardian's challenge), commands (breakthroughUse), step: stage-9 hold + trial (systems/breakthrough.ts)
    breakthrough: new BreakthroughSystem(h),
    // commands (gm): development cheats, heard only in a `gm` world (systems/gm.ts)
    gm: new GmSystem(h),
  } satisfies Record<string, WorldSystem>;
}

export type Systems = ReturnType<typeof createSystems>;

export { actOf, actRoleOf, clearAct, setAct } from './npcActs';
export type { CommandHandlers, CommandOf, PlayerInput, SystemHost, WorldSystem } from './types';
